import { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { useTelegram } from "../../contexts/TelegramContext";
import { tg, errorTg, codigoTg } from "../../lib/telegram";

const SONDEO_MS = 2000;

/** Dibuja el enlace tg://login como código QR (SVG local; el enlace no sale del navegador ni se guarda). */
function Qr({ url }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let vivo = true;
    QRCode.toString(url, { type: "svg", margin: 1, width: 180 })
      .then((svg) => { if (vivo) setSrc(`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`); })
      .catch(() => { if (vivo) setSrc(""); });
    return () => { vivo = false; };
  }, [url]);
  return <div className="tg-qr" data-testid="tg-qr">{src ? <img src={src} alt="Código QR para vincular Telegram" width={180} height={180} /> : <span className="tg-nd">Dibujando…</span>}</div>;
}

/**
 * Conexión de la cuenta de Telegram del dueño (Founder o gerente): su api_id/api_hash propios + código QR (+ clave de dos pasos si la tiene).
 * El api_hash se escribe, se envía y se BORRA del navegador al instante; nunca se vuelve a mostrar.
 */
export default function ConectarTelegram() {
  const { status, refreshStatus } = useTelegram();
  const own = status?.own || {};
  const [apiId, setApiId] = useState("");
  const [apiHash, setApiHash] = useState("");
  const [editando, setEditando] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [fase, setFase] = useState(own.state === "qr_pending" ? "qr" : own.state === "password_required" ? "2fa" : "inicio");   // inicio | qr | 2fa
  const [clave2fa, setClave2fa] = useState("");
  const [error, setError] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [cierreAnterior, setCierreAnterior] = useState(false);
  const vivo = useRef(true);
  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

  const tieneCreds = !!own.has_credentials;
  const mostrarForm = !tieneCreds || editando;

  const guardar = async (ev) => {
    ev.preventDefault();
    if (ocupado) return;
    const id = Number(apiId);
    setOcupado(true); setError("");
    const hash = apiHash;
    setApiHash("");   // se borra del navegador ANTES de esperar la respuesta: no queda en pantalla ni en memoria de la pantalla
    try {
      await tg.put("/account/credentials", { api_id: id, api_hash: hash });
      if (!vivo.current) return;
      setApiId(""); setEditando(false);
      await refreshStatus();
    } catch (e) {
      if (!vivo.current) return;
      setError(errorTg(e)); setCierreAnterior(codigoTg(e) === "already_connected");
    }
    if (vivo.current) setOcupado(false);
  };

  const pedirQr = async () => {
    if (ocupado) return;
    setOcupado(true); setError("");
    try {
      const r = await tg.post("/account/qr");
      if (!vivo.current) return;
      setQrUrl(r.data?.qr_url || ""); setFase("qr");
    } catch (e) { if (vivo.current) { setError(errorTg(e)); setCierreAnterior(codigoTg(e) === "already_connected"); } }
    if (vivo.current) setOcupado(false);
  };

  // sondeo cada ~2 s mientras haya QR o falte la clave de dos pasos; si el QR se renueva, ya viene el nuevo
  const sondear = useCallback(async () => {
    try {
      const r = await tg.get("/account/qr");
      if (!vivo.current) return "seguir";
      const d = r.data || {};
      if (d.state === "conectado") { await refreshStatus(); return "fin"; }
      if (d.state === "requiere_2fa") { setFase("2fa"); return "seguir"; }
      if (d.state === "esperando_qr") { setFase("qr"); setQrUrl((u) => (d.qr_url && d.qr_url !== u ? d.qr_url : u)); }
      return "seguir";
    } catch (e) {
      if (!vivo.current) return "fin";
      if (codigoTg(e) === "no_pending_login") { setFase("inicio"); setQrUrl(""); setError(errorTg(e)); refreshStatus(); return "fin"; }
      setError(errorTg(e));
      return "seguir";
    }
  }, [refreshStatus]);
  useEffect(() => {
    if (fase === "inicio") return undefined;
    let t;
    let activo = true;
    const vuelta = async () => {
      const r = await sondear();
      if (activo && r === "seguir") t = setTimeout(vuelta, SONDEO_MS);
    };
    t = setTimeout(vuelta, SONDEO_MS);
    return () => { activo = false; clearTimeout(t); };
  }, [fase, sondear]);
  // si la página se recargó con un QR en curso, recuperarlo
  useEffect(() => { if (fase === "qr" && !qrUrl) sondear(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const enviar2fa = async (ev) => {
    ev.preventDefault();
    if (ocupado || !clave2fa) return;
    const c = clave2fa;
    setClave2fa(""); setOcupado(true); setError("");
    try {
      await tg.post("/account/2fa", { password: c });
      if (!vivo.current) return;
      await refreshStatus();
    } catch (e) { if (vivo.current) setError(errorTg(e)); }
    if (vivo.current) setOcupado(false);
  };

  const cerrarAnterior = async () => {
    setOcupado(true);
    try { await tg.delete("/account"); setError(""); setCierreAnterior(false); await refreshStatus(); }
    catch (e) { if (vivo.current) setError(errorTg(e)); }
    if (vivo.current) setOcupado(false);
  };

  return (
    <div className="tg-conectar" data-testid="tg-conectar">
      <div className="tg-card">
        <div className="tg-fila"><div className="tg-h2">Conecte su propio Telegram</div><span className="tg-sp" /><span className="tg-pill lock">SOLO SUYO · NADIE MÁS LO VE</span></div>
        {own.state === "session_invalid" && <div className="tg-aviso" role="alert" data-testid="tg-sesion-caida">La sesión anterior se cayó. Vuelva a conectarla con el código QR.</div>}

        <div className="tg-paso"><b>1</b>
          <div className="tg-paso-c">
            <div className="tg-t">Cree su aplicación de Telegram (una sola vez)</div>
            <div className="tg-hint">Entre a my.telegram.org con su número, abra «API development tools» y cree la aplicación. Telegram le da dos datos: el <b>api_id</b> y el <b>api_hash</b>.</div>
            {mostrarForm ? (
              <form className="tg-fila wrap" onSubmit={guardar} data-testid="tg-form-credenciales">
                <input className="tg-in" style={{ maxWidth: 200 }} inputMode="numeric" autoComplete="off" aria-label="api_id" data-testid="tg-api-id" placeholder="api_id" value={apiId} onChange={(e) => setApiId(e.target.value.replace(/\D/g, ""))} />
                <input className="tg-in" style={{ maxWidth: 300 }} type="password" autoComplete="off" spellCheck={false} aria-label="api_hash" data-testid="tg-api-hash" placeholder="api_hash (se guarda cifrado)" value={apiHash} onChange={(e) => setApiHash(e.target.value.trim())} />
                <button type="submit" className="tg-btn pri" data-testid="tg-guardar-cred" disabled={ocupado || !apiId || apiHash.length < 16}>Guardar</button>
                {editando && <button type="button" className="tg-btn" onClick={() => { setEditando(false); setApiHash(""); }}>Cancelar</button>}
              </form>
            ) : (
              <div className="tg-fila wrap" data-testid="tg-cred-guardadas">
                <span className="tg-pill ok">CREDENCIALES GUARDADAS · CIFRADAS</span>{own.api_id ? <span className="tg-hint">api_id {own.api_id}</span> : null}
                <button type="button" className="tg-btn sm" data-testid="tg-cambiar-cred" onClick={() => setEditando(true)}>Cambiar</button>
              </div>
            )}
          </div>
        </div>

        <div className="tg-sep" />
        <div className="tg-paso"><b>2</b>
          <div className="tg-paso-c">
            <div className="tg-t">Escanee el código con su celular</div>
            <div className="tg-hint">En su Telegram: Ajustes → Dispositivos → Vincular dispositivo de escritorio. El código se renueva cada 30 s.</div>
            {fase === "inicio" && <div><button type="button" className="tg-btn pri" data-testid="tg-generar-qr" disabled={!tieneCreds || mostrarForm || ocupado} onClick={pedirQr}>Generar código QR</button></div>}
            {fase === "qr" && (qrUrl ? <Qr url={qrUrl} /> : <span className="tg-nd" data-testid="tg-qr-espera">Esperando el código…</span>)}
            {fase === "2fa" && (
              <form className="tg-fila wrap" onSubmit={enviar2fa} data-testid="tg-form-2fa">
                <input className="tg-in" style={{ maxWidth: 260 }} type="password" autoComplete="off" aria-label="Contraseña de dos pasos" data-testid="tg-2fa" placeholder="Clave en dos pasos de Telegram" value={clave2fa} onChange={(e) => setClave2fa(e.target.value)} />
                <button type="submit" className="tg-btn pri" data-testid="tg-enviar-2fa" disabled={ocupado || !clave2fa}>Continuar</button>
              </form>
            )}
          </div>
        </div>

        <div className="tg-sep" />
        <div className="tg-paso"><b>3</b>
          <div className="tg-paso-c"><div className="tg-t">Listo: aparecen sus chats</div>
            <div className="tg-hint">Verá solo sus conversaciones, en modo lectura. Puede cerrar la sesión cuando quiera desde aquí o desde su celular. Si tiene clave en dos pasos, se pide una sola vez.</div></div>
        </div>
        {error && <div className="tg-err" role="alert" data-testid="tg-error-conectar">{error}{cierreAnterior && <button type="button" className="tg-btn sm" style={{ marginLeft: 8 }} data-testid="tg-cerrar-anterior" disabled={ocupado} onClick={cerrarAnterior}>Cerrar la sesión anterior</button>}</div>}
      </div>
      <div className="tg-fila wrap">
        <span className="tg-pill ok">SU SESIÓN VIVE APARTE DE LA DE LOS DEMÁS</span><span className="tg-pill">EL ADMINISTRADOR NO PUEDE VERLA</span>
      </div>
    </div>
  );
}
